import React from "react";
import { FieldError, ResolverOptions } from "react-hook-form";
import * as z from "zod";

/// generate defaults from Zod schema
export const generateDefaults = (sch: z.ZodSchema<any>): any => {
    // Handle explicit defaults
    if (sch instanceof z.ZodDefault) {
        return sch._def.defaultValue; // Use explicit default if provided
    }

    // Handle effects (e.g., .min(), .regex(), .nonempty()) by unwrapping to inner type
    if (sch instanceof z.ZodEffects) {
        return generateDefaults(sch._def.schema); // Recurse into the inner schema
    }

    // Handle optional or nullable wrappers
    if (sch instanceof z.ZodOptional || sch instanceof z.ZodNullable) {
        return generateDefaults(sch._def.innerType); // Recurse into the inner type
    }

    // Base types
    if (sch instanceof z.ZodObject) {
        const shape = sch.shape;
        return Object.fromEntries(Object.entries(shape).map(([key, value]) => [key, generateDefaults(value as any)]));
    }
    if (sch instanceof z.ZodString) {
        return "";
    }
    if (sch instanceof z.ZodNumber) {
        return 0;
    }
    if (sch instanceof z.ZodBoolean) {
        return false;
    }

    // Fallback for unhandled types (shouldn't happen with this schema)
    console.warn(`Unhandled Zod type in generateDefaults: ${sch.constructor.name}`);
    return undefined;
};

/// This resolver will collect all errors not just the first one.
export const zodResolver =
    (schema: z.ZodSchema<any>) => async (values: any, _context: any, _options: ResolverOptions<any>) => {
        try {
            const data = await schema.parseAsync(values);
            return { values: data, errors: {} };
        } catch (error) {
            const zodError = error as z.ZodError;
            const errors: Record<string, FieldError> = {};

            let customIndex = 1;
            zodError.issues.forEach((issue) => {
                const path = issue.path.join(".");
                if (!errors[path]) {
                    errors[path] = {
                        type: issue.code,
                        message: issue.message,
                        types: {},
                    };
                }
                // Handle duplicate custom codes by indexing
                let key: string = issue.code;
                if (key === z.ZodIssueCode.custom) {
                    key = `${key}_${customIndex++}`;
                }

                errors[path].types![key] = issue.message;
            });

            return { values: values, errors };
        }
    };

/// Creates an appropriate error message for a given field error. It handles when there are multiple error messages.
export function displayError(error?: FieldError): React.ReactNode {
    if (!error) return null;

    const getErrorMessages = (err: FieldError): string[] => {
        if (err.types) {
            return Object.values(err.types).flatMap((msg) => (Array.isArray(msg) ? msg : [msg])) as string[];
        }
        return err.message ? [err.message] : [];
    };

    const errorMessages = getErrorMessages(error);

    if (errorMessages.length === 0) return null;

    if (errorMessages.length > 1) {
        return errorMessages.map((msg, index) => (
            <span key={index} style={{ display: "block" }}>
                {msg}
            </span>
        ));
    } else return errorMessages[0];
}
